import { useState, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { inputIndex, type inputType } from '../types';
import { DebtTypeEnum, FullDebt } from 'src/types/inputs';

export type useDebtStateHandlerType = [
  inputState: Record<string, inputType>,
  handleOnClick: (matches: boolean) => void,
  handleOnChange: (
    idx: string,
    inputIdx: inputIndex,
    value: string | number
  ) => void,
  confirmDebt: (idx: string) => void,
  unConfirmDebt: (idx: string) => void,
  removeDebt: (idx: string) => void,
  closeDialog: (idx: string) => void,
  unavailableInputs: string[]
];

export const defaultDebtInput = (idx: string): inputType => ({
  index: idx,
  [inputIndex.DEBT_TYPE]: DebtTypeEnum.CreditCard,
  [inputIndex.INSTITUTION]: '',
  [inputIndex.AMOUNT]: null,
  confirmed: false,
  isOpen: false,
});

const getInitialDebts = (debts: FullDebt[] = []): Record<string, inputType> =>
  debts.reduce((acc, debt) => {
    const index = uuid();
    return {
      ...acc,
      [index]: {
        index,
        [inputIndex.DEBT_TYPE]: debt.debtType,
        [inputIndex.INSTITUTION]: debt.institution,
        [inputIndex.AMOUNT]: debt.amount,
        confirmed: true,
        isOpen: false,
      },
    };
  }, {});

export const useDebtStateHandler = (
  debts: FullDebt[]
): useDebtStateHandlerType => {
  const [debtIndex, setDebtIndex] = useState<string>('');
  const [inputState, setInputState] = useState<Record<string, inputType>>(
    getInitialDebts(debts)
  );
  const [unavailableInputs, setUnavailableInputs] = useState<string[]>([]);
  const [editingInput, setEditingInput] = useState<inputType>();

  const addNewDebt = (idx = uuid()) => {
    setInputState({
      ...inputState,
      [idx]: defaultDebtInput(idx),
    });
  };

  const confirmDebt = (idx: string): void => {
    setInputState((prevInputState) => ({
      ...prevInputState,
      [idx]: { ...prevInputState[idx], confirmed: true, isOpen: false },
    }));
    setEditingInput(undefined);
  };

  const unConfirmDebt = (idx: string): void => {
    setEditingInput(inputState[idx]);
    setInputState({
      ...inputState,
      [idx]: { ...inputState[idx], confirmed: false, isOpen: true },
    });
  };

  const removeDebt = (idx: string): void => {
    removeFromUnavailableInputs(idx);
    const newInputState = { ...inputState };
    delete newInputState[idx];
    setInputState(newInputState);
  };

  const closeDialog = (idx: string): void => {
    setInputState({
      ...inputState,
      ...(editingInput
        ? { [editingInput.index]: { ...editingInput, isOpen: false } }
        : { [idx]: { ...inputState[idx], isOpen: false } }),
    });
    setEditingInput(undefined);
  };

  const handleOnClick = (matches: boolean): void => {
    if (!matches) {
      const unconfirmedDebt = Object.values(inputState).find(
        (item) => !item.confirmed
      );
      if (!unconfirmedDebt) {
        const newDebtIdx = uuid();
        setInputState({
          ...inputState,
          [newDebtIdx]: { ...defaultDebtInput(newDebtIdx), isOpen: true },
        });
      } else {
        setInputState({
          ...inputState,
          [unconfirmedDebt.index]: {
            ...inputState[unconfirmedDebt.index],
            isOpen: true,
          },
        });
      }
    } else {
      addNewDebt();
    }
  };

  const handleOnChange = (
    idx: string,
    inputIdx: inputIndex,
    value: string | number
  ) => {
    setDebtIndex(idx);
    removeFromUnavailableInputs(idx);
    setInputState({
      ...inputState,
      [idx]: { ...inputState[idx], [inputIdx]: value },
    });
  };

  const removeFromUnavailableInputs = (idx: string): void => {
    const valueToRemove = `${inputState[idx][inputIndex.DEBT_TYPE]}-${
      inputState[idx][inputIndex.INSTITUTION]
    }`;
    const filteredUnavailableInputs = unavailableInputs.filter(
      (item) => item !== valueToRemove
    ) as string[];
    setUnavailableInputs(filteredUnavailableInputs);
  };

  const addToUnavailableInputs = (idx: string): void => {
    if (inputState[idx]) {
      const valueToAdd = `${inputState[idx][inputIndex.DEBT_TYPE]}-${
        inputState[idx][inputIndex.INSTITUTION]
      }`;
      setUnavailableInputs(
        Array.from(new Set(unavailableInputs).add(valueToAdd))
      );
    }
  };
  useEffect(() => {
    addToUnavailableInputs(debtIndex as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputState]);

  return [
    inputState,
    handleOnClick,
    handleOnChange,
    confirmDebt,
    unConfirmDebt,
    removeDebt,
    closeDialog,
    unavailableInputs,
  ];
};
